import {
    DestinationEvent,
    transformDestinationEvent,
} from '@eventbrite/event-renderer';
import { GenericLazyString } from '@eventbrite/i18n';
import { format, startOfToday } from 'date-fns';
import { useMemo } from 'react';
import {
    CATEGORY_CAROUSEL_TITLE_MAPPING,
    DEFAULT_MAX_EVENT_COUNT,
    DEFAULT_MIN_EVENT_COUNT,
    EVENTBRITE_FORMAT_MAPPING,
    FREE,
    NIGHTLIFE,
    TOP,
    WEEKEND,
} from '../../constants';
import { getEventCategoryFormat, isNextWeekend } from '../containerUtils';

interface SingleCategoryEventData {
    events: DestinationEvent[];
    title: GenericLazyString;
    key: string;
}

/**
 * Custom hook that categorizes nearby events based on ordered categories.
 *
 * @param nearbyEvents - Array of nearby destination events.
 * @param nearbyNightlifeEvents - Array of nearby nightlife events.
 * @param nearbyFreeEvents - Array of nearby free events.
 * @param orderedCategories - Array of categories in the desired order. Defaults to empty array.
 * @returns An array of categorized event data.
 */
export const useGetCategoryData = ({
    nearbyEvents,
    nearbyNightlifeEvents,
    nearbyFreeEvents,
    orderedCategories,
}: {
    nearbyEvents: DestinationEvent[];
    nearbyNightlifeEvents: DestinationEvent[];
    nearbyFreeEvents: DestinationEvent[];
    orderedCategories: string[];
}) => {
    // Transform nearbyEvents for easy categorization
    const processedNearbyEvents = useMemo(
        () =>
            nearbyEvents.map((event) => ({
                transformedEvent: transformDestinationEvent(event),
                originalEvent: event,
            })),
        [nearbyEvents],
    );

    // Logic for the category data
    const categoryEventData = useMemo(() => {
        const tempCategoryEventData: { [key: string]: any[] } = {};

        // Initialize temp event category map with empty arrays
        orderedCategories.forEach((category) => {
            tempCategoryEventData[category] = [];
        });

        // Add nightlife events (up to max event count)
        if (orderedCategories.includes(NIGHTLIFE)) {
            tempCategoryEventData[NIGHTLIFE] = nearbyNightlifeEvents.slice(
                0,
                DEFAULT_MAX_EVENT_COUNT,
            );
        }

        if (orderedCategories.includes(FREE)) {
            tempCategoryEventData[FREE] = nearbyFreeEvents.slice(
                0,
                DEFAULT_MAX_EVENT_COUNT,
            );
        }

        // Categorize the nearby events
        processedNearbyEvents.forEach(({ transformedEvent, originalEvent }) => {
            // Get Weekend events
            if (
                transformedEvent.startDate &&
                orderedCategories.includes(WEEKEND)
            ) {
                const today = format(startOfToday(), 'yyyy-MM-dd');
                // Checking if the events are happening before the next Monday (or this weekend), and if they are on a Saturday, Sunday, or Friday.
                if (
                    isNextWeekend(transformedEvent.startDate, today) &&
                    tempCategoryEventData[WEEKEND].length <
                        DEFAULT_MAX_EVENT_COUNT
                ) {
                    tempCategoryEventData[WEEKEND].push(originalEvent);
                }
            }

            // Grab Event Category format for event
            const eventCategory = getEventCategoryFormat(transformedEvent);

            if (eventCategory) {
                const isCarouselEventCategory =
                    eventCategory in EVENTBRITE_FORMAT_MAPPING;
                const categoryKey = EVENTBRITE_FORMAT_MAPPING[eventCategory];
                if (
                    orderedCategories.includes(categoryKey) &&
                    isCarouselEventCategory &&
                    tempCategoryEventData[categoryKey].length <
                        DEFAULT_MAX_EVENT_COUNT
                ) {
                    tempCategoryEventData[categoryKey].push(originalEvent);
                }
            }

            // Add event to top events if there are less than the max event count
            if (
                orderedCategories.includes(TOP) &&
                tempCategoryEventData[TOP].length < DEFAULT_MAX_EVENT_COUNT
            ) {
                tempCategoryEventData[TOP].push(originalEvent);
            }
        });

        // Create the final category event data array
        return orderedCategories
            .map((category) => {
                const events = tempCategoryEventData[category];

                const eventsMeetMinCount =
                    events.length >= DEFAULT_MIN_EVENT_COUNT;

                return events && eventsMeetMinCount
                    ? {
                          key: category,
                          events,
                          title: CATEGORY_CAROUSEL_TITLE_MAPPING[category],
                      }
                    : null;
            })
            .filter(Boolean) as SingleCategoryEventData[];
    }, [
        processedNearbyEvents,
        nearbyNightlifeEvents,
        nearbyFreeEvents,
        orderedCategories,
    ]);

    return categoryEventData;
};
